import CloseIcon from '@mui/icons-material/Close';
import { Divider, GlobalStyles } from '@mui/material';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import makeStyles from '@mui/styles/makeStyles';
import React from 'react';
import { v4 as uuid } from 'uuid';

import useMixPanel from '@/hooks/useMixPanel';

import { getUserId } from '../../auth';
import { Button } from '../../components/Button';
import { ErrorBoundary } from '../../components/ErrorBoundary';
// Types
import type { Address } from '../../types';
import { create as createUnit } from '../../Units/api';
import type { PipeDiameter, PipeMaterial, Unit } from '../../Units/types';
import { create as createStructure } from '../api';
import type { Structure } from '../types';
import { useStructures } from '../useStructures';
import { addressIsValid, createUnits, getNextIndex, getPrevIndex, isUnitValid } from '../util';
import { Buttons } from './Buttons';
import { Component } from './Component';
import { actions, initialValue, reducer } from './reducer';

const useDialogStyles = makeStyles({
  paper: {
    minHeight: 560,
    borderRadius: 10,
  },
});

// Util ///////////////////////////////////////////////////////////////////////

const emptyAddress: Address = {
  street: '',
  street2: '',
  city: '',
  state: '',
  zip: '',
};

// Steps in form
const WELCOME = 0;
const STRUCTURE = 1;
const UNITS = 2;
const REVIEW = 3;
const CREATE = 4;

const buttonLabels = [
  /* 0 */ 'Welcome',
  /* 1 */ 'Property Details',
  /* 2 */ 'Unit Details',
  /* 3 */ 'Review',
  /* 4 */ 'Create',
  /* 5 */ 'Done',
];

const create = (userId: string, structure: Structure, units: Unit[]) => {
  const clonedStruct = {
    name: structure.name,
    address: structure.address,
    ix: structure.ix,
    rate_method: structure.rate_method,
    timezone_key: structure.timezone_key,
  };
  // Create structure
  return createStructure(userId, clonedStruct).then((structure: Structure) => {
    // Create units
    return (
      Promise.all(
        units.map((unit, index) =>
          createUnit(userId, structure.id, structure.address, unit.name, index),
        ),
      )
        // Combine returned data
        .then((units) => {
          return { structure, units };
        })
    );
  });
};

// Sub Components /////////////////////////////////////////////////////////////

/**
 * Error dialog
 *
 * @param {object} props - Component properties
 * @param {boolean} props.open - Whether or not dialog is open
 * @param {Function} props.onClose - Callback for close request
 * @param {string} props.title - Dialog title
 * @returns {React.Component} - Dialog
 */
const ErrorDialog = (props: { open: boolean; onClose: () => any; title?: string }) => (
  <Dialog open={props.open} onClose={props.onClose}>
    <DialogTitle>{props.title}</DialogTitle>
    <DialogContent>{'The issue has been logged and recorded'}</DialogContent>
    <DialogActions>
      <Button onClick={props.onClose} color={'primary'}>
        {'Close'}
      </Button>
    </DialogActions>
  </Dialog>
);

// Component //////////////////////////////////////////////////////////////////

const structureFormDialogClasses = makeStyles({
  buttonContainer: {
    justifyContent: 'space-between',
  },
  firstButtonContainer: {
    justifyContent: 'center',
  },
  lastButtonContainer: {
    justifyContent: 'right',
  },
});

type Props = {
  open?: boolean;
  onClose?: () => void;
};

/**
 * StructureForm
 *
 * @param {object} props - Component properties
 * @returns {React.Component} - Structure form
 */
export const StructureForm = ({ open, onClose = () => {} }: Props): JSX.Element => {
  // Constants ////////////////////////

  const classes = structureFormDialogClasses();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));

  // Data /////////////////////////////

  const { structures, mutate } = useStructures();

  // State ////////////////////////////

  const [state, dispatch] = React.useReducer(reducer, initialValue);
  const [stepIndex, setStepIndex] = React.useState(WELCOME);
  const [currentAddress, setCurrentAddress] = React.useState(emptyAddress);
  const [currentStructureId, setCurrentStructureId] = React.useState<string | null>(null);
  const [structureName, setStructureName] = React.useState('');
  const [numberOfUnits, setNumberOfUnits] = React.useState(1);
  const [unitErrorObj, setUnitErrorObj] = React.useState({});
  const [isStepValid, setIsStepValid] = React.useState(true);
  const [errorMessage, setErrorMessage] = React.useState<string>();
  const [rateMethod, setRateMethod] = React.useState<'SIMPLESUB' | 'UTILITY'>('SIMPLESUB');
  const [createLoading, setCreateLoading] = React.useState(false);
  const [timeZone, setTimeZone] = React.useState<string>('US/Eastern');
  const { trackEvent } = useMixPanel();

  const isValid = React.useCallback(
    (index: number) => {
      // Welcome
      if (index === WELCOME) {
        return true;
      }
      // Structure
      else if (index === STRUCTURE) {
        return Boolean(
          addressIsValid(currentAddress) && structureName && numberOfUnits && numberOfUnits > 0,
        );
      }
      // Units
      else if (index === UNITS) {
        return Boolean(
          currentStructureId && setUnitsValid(state.structures[currentStructureId].units),
        );
      }
      // Review
      else if (index === REVIEW) {
        return true;
      }
      // Create
      else if (index === CREATE) {
        return true;
      }
      return false;
    },
    [currentAddress, currentStructureId, numberOfUnits, structureName, state.structures],
  );

  // Effects //////////////////////////

  // Re-run validation
  React.useEffect(() => {
    setIsStepValid(isValid(stepIndex));
  }, [stepIndex, isValid]);

  // Reset units if user goes back to edit number of units
  React.useEffect(() => {
    if (currentStructureId) {
      const newUnits = createUnits(numberOfUnits);

      dispatch({
        type: 'SET_UNITS',
        payload: { structureId: currentStructureId, units: newUnits },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [numberOfUnits]);

  // Util /////////////////////////////

  /**
   * Given an an object of units, determine if they're all valid
   *
   * @param {object} units - An object of Units
   * @returns {boolean} - Wether or not all units are valid
   */
  const setUnitsValid = (units) => {
    let areAllUnitsValid = true;
    const unitErrorObj = units.reduce((acc, cur) => {
      const valid = isUnitValid(cur);
      if (!valid) {
        areAllUnitsValid = false;
      }
      return { ...acc, [cur.id]: !valid };
    }, {});

    setUnitErrorObj(unitErrorObj);
    return areAllUnitsValid;
  };

  /** Reset form */
  const resetForm = () => {
    setStepIndex(WELCOME);
    setCurrentStructureId(null);
    setStructureName('');
    setNumberOfUnits(1);
    setCurrentAddress(emptyAddress);
    setRateMethod('SIMPLESUB');
    setTimeZone('US/Eastern');
    setUnitErrorObj({});
    setIsStepValid(true);
    setErrorMessage(undefined);
    setCreateLoading(false);
    dispatch({
      type: actions.RESET_STRUCTURE,
      payload: null,
    });
  };

  /** Reset form for adding another property */
  const onAddAnother = () => {
    setStepIndex(1);
    setCurrentStructureId(null);
    setStructureName('');
    setNumberOfUnits(1);
    setCurrentAddress(emptyAddress);
    setRateMethod('SIMPLESUB');
  };

  /** Handle modal close with form reset */
  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handlePropertyEvents = async ({
    structure,
    units,
  }: {
    structure: Structure;
    units: Unit[];
  }) => {
    await trackEvent('Property Created', {
      PropertyName: structure.name,
      PropertyID: structure.id,
    });
    await units.map(async (e) => {
      await trackEvent('Unit Created', {
        PropertyName: structure.name,
        PropertyID: structure.id,
        UnitName: e.name,
        UnitID: e.id,
      });
    });
    return;
  };

  /** Handle next click */
  const onNext = async () => {
    // Only move forward if section is valid
    if (isStepValid) {
      // Welcome
      if (stepIndex === WELCOME) {
        setStepIndex(getNextIndex(stepIndex, buttonLabels.length));
      }
      // Create structure
      else if (stepIndex === STRUCTURE) {
        if (!currentStructureId || !state.structures[currentStructureId]) {
          const id = uuid();

          dispatch({
            type: actions.CREATE_STRUCTURE,
            payload: {
              name: structureName,
              address: currentAddress,
              numberOfUnits,
              units: createUnits(numberOfUnits),
              id,
              rate_method: rateMethod,
              timeZone: timeZone,
            },
          });

          setCurrentStructureId(id);
        }
        setCurrentAddress(currentAddress);
        setStepIndex(getNextIndex(stepIndex, buttonLabels.length));
      }
      // Setup units
      else if (stepIndex === UNITS) {
        setCurrentAddress(currentAddress);
        setStepIndex(getNextIndex(stepIndex, buttonLabels.length));
      }
      // Review
      else if (stepIndex === REVIEW) {
        if (currentStructureId && state.structures[currentStructureId]) {
          setCreateLoading(true);
          const userId = await getUserId();
          create(
            userId,
            {
              ...state.structures[currentStructureId],
              ix: structures?.length || 0,
              timezone_key: timeZone,
            },
            state.structures[currentStructureId].units,
          )
            .then(async (data) => {
              await handlePropertyEvents(data);
              setStepIndex(getNextIndex(stepIndex, buttonLabels.length));
            })
            .then(() => mutate())
            .catch(() => setErrorMessage('Trouble creating property'))
            .finally(() => {
              setCreateLoading(false);
            });
        } else {
          throw new Error('MISSING_STRUCTURE_ID_OR_STRUCTURE');
        }
      }
      // Done screen
      else if (stepIndex === CREATE) {
        onClose();
        setStepIndex(WELCOME);
        setCurrentStructureId(null);
        setStructureName('');
        setNumberOfUnits(1);
        setCurrentAddress(emptyAddress);
        setRateMethod('SIMPLESUB');
        dispatch({
          type: actions.RESET_STRUCTURE,
          payload: null,
        });
      }
    }
  };

  /** Handle prev click */
  const onPrev = () => {
    setStepIndex(getPrevIndex(stepIndex, 0));
  };

  /**
   * Handle unit name change
   *
   * @param  {string} unitId  - Unit ID
   * @param  {string} newName - New name for the unit
   * @throws Will throw `CANT_FIND_UNIT_TO_EDIT_NAME` if the provided unit ID can't be found
   */
  const onUnitNameChange = (unitId, newName) => {
    const currentUnit =
      currentStructureId &&
      state.structures[currentStructureId].units.find((unit) => unit.id === unitId);
    if (currentUnit) {
      dispatch({
        type: actions.SET_UNIT,
        payload: {
          structureId: currentStructureId,
          unitId,
          unitData: { ...currentUnit, name: newName },
        },
      });
      setIsStepValid(isValid(stepIndex));
    } else {
      throw new Error('CANT_FIND_UNIT_TO_EDIT_NAME');
    }
  };

  /**
   * Handle user changing the pipe size on a single unit
   *
   * @param  {string} unitId      - Unit ID to update
   * @param  {number} newPipeSize - New pipe size
   * @throws Will throw `CANT_FIND_UNIT_TO_EDIT_PIPESIZE` if the provided unit ID can't be found
   */
  const onUnitPipeSizeChange = (unitId: string, newPipeSize: PipeDiameter) => {
    const currentUnit =
      currentStructureId &&
      state.structures[currentStructureId].units.find((unit) => unit.id === unitId);
    if (currentUnit) {
      dispatch({
        type: actions.SET_UNIT,
        payload: {
          structureId: currentStructureId,
          unitId,
          unitData: { ...currentUnit, pipeSize: newPipeSize },
        },
      });
    } else {
      throw new Error('CANT_FIND_UNIT_TO_EDIT_PIPESIZE');
    }
  };

  /**
   * Handle user changing the pipe type on a single unit
   *
   * @param  {string} unitId      - Unit ID to update
   * @param  {number} newPipeType - New pipe type
   * @throws Will throw `CANT_FIND_UNIT_TO_EDIT_PIPESIZE` if the provided unit ID can't be found
   */
  const onUnitPipeTypeChange = (unitId: string, newPipeType: PipeMaterial) => {
    const currentUnit =
      currentStructureId &&
      state.structures[currentStructureId].units.find((unit) => unit.id === unitId);
    if (currentUnit) {
      dispatch({
        type: actions.SET_UNIT,
        payload: {
          structureId: currentStructureId,
          unitId,
          unitData: { ...currentUnit, pipeMaterial: newPipeType },
        },
      });
    } else {
      throw new Error('CANT_FIND_UNIT_TO_EDIT_PIPESIZE');
    }
  };
  const dialogClasses = useDialogStyles();
  const STEP_TITLES: Record<number, string> = {
    [WELCOME]: 'Add a new property',
    [STRUCTURE]: 'Property Details',
    [UNITS]: 'Unit Details',
    [REVIEW]: 'Review',
    [CREATE]: 'Property Created',
  };

  const STEP_SUBTITLES: Record<number, string | undefined> = {
    [STRUCTURE]: 'Please provide information about your property.',
    [UNITS]: 'Please provide names for each unit.',
    [REVIEW]: 'Please verify the below information. If you need to make changes, please go back.',
    [CREATE]: 'Congratulations on adding a new property.',
  };
  // Component ////////////////////////
  return (
    <ErrorBoundary>
      <Dialog
        fullScreen={fullScreen}
        aria-labelledby="structure-create-form-dialog"
        open={!!open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        classes={{
          paper: dialogClasses.paper,
        }}
      >
        <GlobalStyles
          styles={(theme) => ({
            '.MuiOutlinedInput-root': {
              borderRadius: '14px !important',
              minHeight: '45px !important',
              padding: '0 !important',
            },
            '.MuiOutlinedInput-input': {
              padding: '8px 12px !important',
              lineHeight: '1.5em',
            },
            '.MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
              boxShadow: '0 0 0 2px rgba(71,144,204,0.2) !important',
              borderColor: `${theme.palette.primary.main} !important`,
            },
            '.MuiSelect-outlined .MuiOutlinedInput-notchedOutline': {
              borderRadius: '14px !important',
            },
          })}
        />

        <DialogTitle
          id="structure-create-form-dialog"
          component="div"
          sx={{
            textAlign: 'center',
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <div>
            <Typography variant="h4" sx={{ mt: 2 }}>
              {STEP_TITLES[stepIndex]}
            </Typography>
            {STEP_SUBTITLES[stepIndex] && (
              <Typography variant="body2" sx={{ mt: 1 }}>
                {STEP_SUBTITLES[stepIndex]}
              </Typography>
            )}
          </div>{' '}
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <Divider variant="fullWidth" sx={{ borderWidth: 1, borderColor: 'grey' }} />

        <DialogContent>
          <Component
            onAddAnother={onAddAnother}
            onDone={handleClose}
            currentAddress={currentAddress}
            numberOfUnits={numberOfUnits}
            sectionIndex={stepIndex}
            rateMethod={rateMethod}
            setRateMethod={setRateMethod}
            setCurrentAddress={setCurrentAddress}
            setNumberOfUnits={setNumberOfUnits}
            setStructureName={setStructureName}
            state={state}
            structureName={structureName}
            unitErrorObj={unitErrorObj}
            units={currentStructureId ? state.structures[currentStructureId].units : []}
            onUnitNameChange={onUnitNameChange}
            onUnitPipeSizeChange={onUnitPipeSizeChange}
            onUnitPipeTypeChange={onUnitPipeTypeChange}
            timeZone={timeZone}
            onTimeZoneChange={(i) => setTimeZone(i)}
          />
        </DialogContent>

        <Divider variant="fullWidth" sx={{ borderWidth: 1, borderColor: 'grey' }} />

        <DialogActions
          className={
            stepIndex === 0
              ? classes.firstButtonContainer
              : stepIndex === 4
              ? classes.lastButtonContainer
              : classes.buttonContainer
          }
        >
          {/* <div className={classes.headerContainer}>
        <Button color={'primary'} className={classes.addButton} onClick={props.onAddAnother}>
          Add Another
          <AddIcon />
        </Button>
      </div> */}
          {stepIndex !== CREATE ? (
            <Buttons
              isNextDisabled={!isStepValid || createLoading}
              nextLabel={stepIndex ? buttonLabels[stepIndex + 1] : 'Get Started'}
              onNext={onNext}
              onPrev={onPrev}
              prevLabel={stepIndex !== 4 ? buttonLabels[stepIndex - 1] : ''}
              showNextChevron={stepIndex !== buttonLabels.length - 2}
              createLoading={createLoading}
            />
          ) : (
            <>
              <Button variant="outlined" onClick={handleClose} autoFocus>
                Done
              </Button>
              <Button color="primary" onClick={onAddAnother} sx={{ mr: 1 }}>
                Add Another
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
      <ErrorDialog
        open={Boolean(errorMessage)}
        title={errorMessage}
        onClose={() => setErrorMessage(undefined)}
      />
    </ErrorBoundary>
  );
};

export default StructureForm;
